/* ==========================================================================
   AL-QAMAR GEMS — motion engine + cart
   Scroll reveals, masked headlines, parallax, marquee, AJAX cart drawer and
   the mobile sticky buy bar. Every effect degrades to working HTML without
   JavaScript or under reduced-motion.
   ========================================================================== */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ---- reveal on scroll ------------------------------------------------- */
  function initReveal(root) {
    var nodes = (root || document).querySelectorAll('[data-aq-reveal], [data-aq-mask]');
    if (!nodes.length) return;

    if (reduced || !('IntersectionObserver' in window)) {
      nodes.forEach(function (n) { n.classList.add('is-in'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.12 });

    nodes.forEach(function (n) { io.observe(n); });
  }

  /* ---- stagger children -------------------------------------------------- */
  function initStagger(root) {
    (root || document).querySelectorAll('[data-aq-stagger]').forEach(function (group) {
      var step = parseInt(group.getAttribute('data-aq-stagger'), 10) || 90;
      Array.prototype.forEach.call(group.children, function (child, i) {
        child.style.setProperty('--aq-delay', (i * step) + 'ms');
      });
    });
  }

  /* ---- pointer + scroll parallax ---------------------------------------- */
  function initParallax(root) {
    if (reduced) return;
    var layers = (root || document).querySelectorAll('[data-aq-parallax]');
    if (!layers.length) return;

    var mx = 0, my = 0, raf = null;

    function paint() {
      raf = null;
      layers.forEach(function (el) {
        var depth = parseFloat(el.getAttribute('data-aq-parallax')) || 0.2;
        var rect = el.getBoundingClientRect();
        var progress = (rect.top + rect.height / 2 - window.innerHeight / 2) / window.innerHeight;
        var ty = -progress * depth * 130;
        el.style.transform =
          'translate3d(' + (mx * depth * 22).toFixed(2) + 'px,' +
          (ty + my * depth * 18).toFixed(2) + 'px,0)';
      });
    }

    function request() { if (!raf) raf = requestAnimationFrame(paint); }

    window.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', request);
    window.addEventListener('pointermove', function (e) {
      mx = (e.clientX / window.innerWidth) * 2 - 1;
      my = (e.clientY / window.innerHeight) * 2 - 1;
      request();
    }, { passive: true });

    request();
  }

  /* ---- marquee ----------------------------------------------------------- */
  function initMarquee(root) {
    (root || document).querySelectorAll('[data-aq-marquee]').forEach(function (track) {
      if (track.dataset.aqCloned === 'true') return;
      var original = track.innerHTML;
      var copies = Math.max(2, Math.ceil((window.innerWidth * 2) / Math.max(track.scrollWidth, 1)));
      for (var i = 0; i < copies; i++) track.insertAdjacentHTML('beforeend', original);
      track.dataset.aqCloned = 'true';
    });
  }

  /* ======================================================================
     CART DRAWER
     Talks to Shopify's AJAX cart API. If any request fails the handler
     re-submits the form natively, so a customer can always still buy.
     ====================================================================== */
  var Cart = (function () {
    var drawer, list, foot, totalEl, emptyEl, currency, locale, poa;
    var busy = false;

    function els() {
      drawer = document.getElementById('aq-cart-drawer');
      if (!drawer) return false;
      list = drawer.querySelector('[data-aq-cd-list]');
      foot = drawer.querySelector('[data-aq-cd-foot]');
      totalEl = drawer.querySelector('[data-aq-cd-total]');
      emptyEl = drawer.querySelector('[data-aq-cd-empty]');
      currency = drawer.dataset.currency || 'USD';
      locale = document.documentElement.lang || 'en';
      poa = drawer.dataset.poa || 'Price on request';
      return true;
    }

    function money(cents) {
      if (!cents) return poa;
      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency', currency: currency, maximumFractionDigits: 0
        }).format(cents / 100);
      } catch (e) {
        return (cents / 100).toFixed(0);
      }
    }

    function thumb(url, w) {
      if (!url) return '';
      return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
    }

    function setCount(n) {
      document.querySelectorAll('[data-aq-count]').forEach(function (el) {
        el.textContent = n;
        el.classList.toggle('is-empty', n === 0);
      });
    }

    function render(cart) {
      if (!list) return;
      setCount(cart.item_count);

      if (!cart.items.length) {
        list.innerHTML = '';
        if (foot) foot.hidden = true;
        if (emptyEl) emptyEl.hidden = false;
        return;
      }
      if (emptyEl) emptyEl.hidden = true;
      if (foot) foot.hidden = false;

      list.innerHTML = cart.items.map(function (item) {
        var props = '';
        if (item.properties) {
          Object.keys(item.properties).forEach(function (k) {
            var val = item.properties[k];
            if (!val || k.charAt(0) === '_') return;
            props += '<span class="aq-cd__prop">' + esc(k) + ': <em>' + esc(val) + '</em></span>';
          });
        }

        var variant = (item.variant_title && item.variant_title !== 'Default Title')
          ? '<span class="aq-cd__var">' + esc(item.variant_title) + '</span>' : '';

        return '' +
          '<div class="aq-cd__item" data-key="' + esc(item.key) + '">' +
            '<a class="aq-cd__img" href="' + esc(item.url) + '">' +
              (item.image ? '<img src="' + esc(thumb(item.image, 200)) + '" alt="" loading="lazy">' : '') +
            '</a>' +
            '<div class="aq-cd__info">' +
              '<a class="aq-cd__name" href="' + esc(item.url) + '">' + esc(item.product_title) + '</a>' +
              variant + props +
              '<div class="aq-cd__ctrl">' +
                '<button type="button" data-aq-cd-qty="' + (item.quantity - 1) + '" aria-label="Decrease quantity">−</button>' +
                '<span>' + item.quantity + '</span>' +
                '<button type="button" data-aq-cd-qty="' + (item.quantity + 1) + '" aria-label="Increase quantity">+</button>' +
                '<button type="button" class="aq-cd__rm" data-aq-cd-qty="0">Remove</button>' +
              '</div>' +
            '</div>' +
            '<span class="aq-cd__price">' + money(item.final_line_price) + '</span>' +
          '</div>';
      }).join('');

      if (totalEl) totalEl.textContent = money(cart.total_price);
    }

    function open() {
      if (!drawer) return;
      drawer.hidden = false;
      drawer.setAttribute('aria-hidden', 'false');
      requestAnimationFrame(function () { drawer.classList.add('is-open'); });
      document.body.style.overflow = 'hidden';
      var close = drawer.querySelector('[data-aq-cd-close]');
      if (close) close.focus();
    }

    function close() {
      if (!drawer) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      setTimeout(function () { drawer.hidden = true; }, 420);
    }

    function refresh(then) {
      fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) { render(cart); if (then) then(cart); })
        .catch(function () { /* leave last known state */ });
    }

    function change(key, qty) {
      if (busy) return;
      busy = true;
      drawer.classList.add('is-busy');
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty })
      })
        .then(function (r) { return r.json(); })
        .then(function (cart) { render(cart); })
        .catch(function () { window.location.href = '/cart'; })
        .finally(function () { busy = false; drawer.classList.remove('is-busy'); });
    }

    function init() {
      if (!els()) return;

      /* open / close */
      document.addEventListener('click', function (e) {
        var opener = e.target.closest('[data-aq-cart-open]');
        if (opener) { e.preventDefault(); refresh(); open(); return; }
        if (e.target.closest('[data-aq-cd-close]')) { e.preventDefault(); close(); }
      });

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && drawer && !drawer.hidden) close();
      });

      /* quantity + remove */
      drawer.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-aq-cd-qty]');
        if (!btn) return;
        var item = btn.closest('[data-key]');
        if (!item) return;
        change(item.dataset.key, parseInt(btn.dataset.aqCdQty, 10));
      });

      /* intercept add-to-cart */
      document.addEventListener('submit', function (e) {
        var form = e.target.closest('form[action*="/cart/add"]');
        if (!form) return;

        e.preventDefault();
        var btn = form.querySelector('[type="submit"]');
        var label = btn ? btn.textContent : '';
        if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Accept': 'application/json' },
          body: new FormData(form)
        })
          .then(function (r) {
            if (!r.ok) throw new Error('add failed');
            return r.json();
          })
          .then(function () { refresh(); open(); })
          .catch(function () {
            /* Something went wrong — fall back to a normal page POST so the
               customer is never stuck unable to buy. */
            form.submit();
          })
          .finally(function () {
            if (btn) { btn.disabled = false; btn.textContent = label; }
          });
      });

      refresh();
    }

    return { init: init, refresh: refresh, open: open };
  })();

  /* ======================================================================
     MOBILE STICKY BUY BAR
     Appears on product pages once the real buy block scrolls out of view.
     It does not duplicate the form — it forwards the tap to the real button,
     so variant state and validation stay in one place.
     ====================================================================== */
  function initBuyBar() {
    var pdp = document.querySelector('.aq-pdp');
    if (!pdp) return;

    var buy = pdp.querySelector('.aq-pdp__buy');
    var addBtn = pdp.querySelector('[data-aq-add]');
    var titleEl = pdp.querySelector('.aq-pdp__title');
    var priceEl = pdp.querySelector('[data-aq-price]');
    if (!buy || !addBtn || !titleEl) return;

    var bar = document.createElement('div');
    bar.className = 'aq-buybar';
    bar.innerHTML =
      '<div class="aq-buybar__txt">' +
        '<span class="aq-buybar__name">' + esc(titleEl.textContent.trim()) + '</span>' +
        '<span class="aq-buybar__price"></span>' +
      '</div>' +
      '<button type="button" class="aq-btn aq-buybar__btn"></button>';
    document.body.appendChild(bar);

    var barPrice = bar.querySelector('.aq-buybar__price');
    var barBtn = bar.querySelector('.aq-buybar__btn');

    function mirror() {
      if (priceEl) barPrice.textContent = priceEl.textContent.trim();
      barBtn.textContent = addBtn.textContent.trim();
      barBtn.disabled = addBtn.disabled;
    }
    mirror();

    barBtn.addEventListener('click', function () { addBtn.click(); });

    if ('MutationObserver' in window) {
      var mo = new MutationObserver(mirror);
      if (priceEl) mo.observe(priceEl, { childList: true, characterData: true, subtree: true });
      mo.observe(addBtn, { childList: true, attributes: true, attributeFilter: ['disabled'] });
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          bar.classList.toggle('is-in', !entry.isIntersecting && entry.boundingClientRect.top < 0);
        });
      }, { threshold: 0 });
      io.observe(buy);
    }
  }

  /* ---- boot -------------------------------------------------------------- */
  function boot(root) {
    initStagger(root);
    initReveal(root);
    initParallax(root);
    initMarquee(root);
  }

  function bootOnce() {
    boot(document);
    Cart.init();
    initBuyBar();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootOnce);
  } else {
    bootOnce();
  }

  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
